import React, { useId, isValidElement, cloneElement, HTMLAttributes, Children, ReactNode, ComponentProps } from 'react';
import { Form } from 'antd';
import styled from '@emotion/styled';
import { SessionForm } from '@components/Clinicians/session-form';

// Wrapper updated to fix accessibility (screen reader) issues caused by Styled-components and AntD's Form.Item.
// See VLNHS-6868 for implementation notes

type SessionFormItemType = typeof Form.Item<SessionForm>;
type SessionFormItemProps = ComponentProps<SessionFormItemType> & {
  isGroupField?: boolean;
  linkAria?: boolean;
};

const StyledFormItem = styled<SessionFormItemType>(Form.Item)`
  .ant-form-item-label {
    label {
      white-space: normal;
      line-height: 1em;
    }
  }
`;

const defaultProps: Partial<SessionFormItemProps> = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 24 },
    md: { span: 10 },
    lg: { span: 10 },
    xl: { span: 10 },
    xxl: { span: 10 }
  },
  labelAlign: 'left',
  colon: false
};

export const SessionFormItem = (props: SessionFormItemProps) => {
  const { label, linkAria, isGroupField, children, htmlFor, required, rules, ...rest } = { ...defaultProps, ...props };

  const accessibleId = useId();

  const isFieldMandatory = required || rules?.some((rule: any) => rule.required);

  if (typeof children === 'function') {
    return (
      <StyledFormItem label={label} required={required} {...rest}>
        {children}
      </StyledFormItem>
    );
  }

  if (!linkAria && !isGroupField) {
    if (isFieldMandatory && isValidElement(children)) {
      const fixedChildren = cloneElement(children, {
        'aria-required': 'true'
      } as HTMLAttributes<HTMLElement>);

      return (
        <StyledFormItem label={label} required={required} {...rest}>
          {fixedChildren}
        </StyledFormItem>
      );
    }
    return (
      <StyledFormItem label={label} required={required} {...rest}>
        {children}
      </StyledFormItem>
    );
  }

  let finalLabel = label;
  let finalHtmlFor = htmlFor;

  if (isGroupField && label) {
    finalLabel = <span id={accessibleId}>{label}</span>;
  } else if (linkAria) {
    finalHtmlFor = accessibleId;
  }

  const accessibleChildren = Children.map(children as ReactNode, (child) => {
    if (!isValidElement(child)) {
      return child;
    }

    const childProps: any = {};

    if (isGroupField && label) {
      childProps['aria-labelledby'] = accessibleId;
      childProps.role = 'radiogroup';
    } else if (linkAria) {
      childProps.id = accessibleId;
    }

    if (isFieldMandatory) {
      childProps['aria-required'] = 'true';
    }

    return cloneElement(child, childProps);
  });

  return (
    <StyledFormItem label={finalLabel} htmlFor={finalHtmlFor} required={required} rules={rules} {...rest}>
      {accessibleChildren}
    </StyledFormItem>
  );
};
